import { Fragment } from 'react';
import SocialMeta from '#components/common/meta/SocialMeta';
import SliderDocumentBlockRenderer from '#components/sections/slider-document-block/SliderDocumentBlock';
import TextDocumentBlockRenderer from '#components/sections/text-document-block/TextDocumentBlock';
import LivestreamBannerRenderer from '#components/sections/livestream-banner/LivestreamBanner';
import {
  BannerDocumentBlock,
  ContinueWatchingDocumentBlock,
  LivestreamBannerDocumentBlock,
  RasterDocumentBlock,
  RemoteContentDocumentBlock,
  SliderDocumentBlock,
  TeaserLaneDocumentBlock,
  TeaserLaneGenericDocumentBlock,
  TextDocumentBlock,
  Theme,
} from '#types/generated/graphql-types';
import dynamic from 'next/dynamic';

type Props = {
  blocks: Array<
    | BannerDocumentBlock
    | ContinueWatchingDocumentBlock
    | LivestreamBannerDocumentBlock
    | RasterDocumentBlock
    | RemoteContentDocumentBlock
    | SliderDocumentBlock
    | TeaserLaneDocumentBlock
    | TeaserLaneGenericDocumentBlock
    | TextDocumentBlock
  >;
  description?: string;
  title?: string;
  theme?: Theme;
};

const TeaserLaneDocumentBlockRenderer = dynamic(
  () =>
    import('#components/sections/teaser-lane-document-block/TeaserLaneDocumentBlock'),
  { ssr: false }
);

const TeaserLaneGenericDocumentBlockRenderer = dynamic(
  () =>
    import('#components/sections/teaser-lane-generic-document-block/TeaserLaneGenericDocumentBlock'),
  { ssr: false }
);

const BannerDocumentBlockRenderer = dynamic(
  () =>
    import('#components/sections/banner-document-block/BannerDocumentBlock'),
  { ssr: false }
);

const ContinueWatchingDocumentBlockRenderer = dynamic(
  () =>
    import('#components/sections/continue-watching-document-block/ContinueWatchingDocumentBlock'),
  { ssr: false }
);

const RasterDocumentBlockRenderer = dynamic(
  () =>
    import('#components/sections/raster-document-block/RasterDocumentBlock'),
  { ssr: false }
);

const DocumentPublished = ({ blocks, description, title, theme }: Props) => {
  const renderBlocks = () => {
    return blocks.map((item, index) => {
      if (item.__typename === 'SliderDocumentBlock') {
        return <SliderDocumentBlockRenderer block={item} key={item.id} />;
      }

      if (item.__typename === 'BannerDocumentBlock') {
        return <BannerDocumentBlockRenderer block={item} key={item.id} />;
      }

      if (item.__typename === 'ContinueWatchingDocumentBlock') {
        return (
          <ContinueWatchingDocumentBlockRenderer block={item} key={item.id} />
        );
      }

      if (item.__typename === 'LivestreamBannerDocumentBlock') {
        return <LivestreamBannerRenderer block={item} key={item.id} />;
      }

      if (item.__typename === 'RasterDocumentBlock') {
        return (
          <RasterDocumentBlockRenderer
            block={item}
            key={item.id}
            isTopBlock={index === 0}
          />
        );
      }

      if (item.__typename === 'TeaserLaneDocumentBlock') {
        return <TeaserLaneDocumentBlockRenderer block={item} key={item.id} />;
      }

      if (item.__typename === 'TeaserLaneGenericDocumentBlock') {
        return (
          <TeaserLaneGenericDocumentBlockRenderer block={item} key={item.id} />
        );
      }

      if (
        item.__typename === 'RemoteContentDocumentBlock' ||
        item.__typename === 'TextDocumentBlock'
      ) {
        return (
          <TextDocumentBlockRenderer
            block={item}
            key={item.id}
            theme={theme}
            isTopBlock={index === 0}
          />
        );
      }
    });
  };

  return (
    <Fragment>
      <SocialMeta
        about={{
          title: title,
          description: description,
        }}
      />
      <main className="page-main-container tw-pb-5">{renderBlocks()}</main>
    </Fragment>
  );
};

export default DocumentPublished;
